import React, { useState, useEffect } from "react";
import "./TabSelector.css";

const TabSelector = ({
  tabs = [],
  onTabChange,
  onSearch,
  withSearch = false,
  defaultActiveTab = null,
  showUpcoming = true,
  showActive = true,
  showHistory = true,
  badgeCounts = {},
}) => {
  const [activeTab, setActiveTab] = useState(() => {
    if (defaultActiveTab) return defaultActiveTab;

    if (showUpcoming && tabs.find((tab) => tab.id === "upcoming"))
      return "upcoming";
    if (showActive && tabs.find((tab) => tab.id === "active")) return "active";
    if (showHistory && tabs.find((tab) => tab.id === "history"))
      return "history";

    return tabs.length > 0 ? tabs[0].id : null;
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 768 && window.innerWidth < 1024
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (onSearch) {
      onSearch(value);
    }
  };

  const handleSearchClear = () => {
    setSearchQuery("");
    if (onSearch) {
      onSearch("");
    }
  };

  const toggleMobileSearch = () => {
    setShowMobileSearch(!showMobileSearch);
    if (showMobileSearch && searchQuery) {
      handleSearchClear();
    }
  };

  const handleMobileSearchSubmit = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchQuery);
    }
  };

  // Filter tabs based on visibility props
  const visibleTabs = tabs.filter((tab) => {
    if (tab.id === "upcoming" && !showUpcoming) return false;
    if (tab.id === "active" && !showActive) return false;
    if (tab.id === "history" && !showHistory) return false;
    return true;
  });

  return (
    <>
      <div className="tab-selector-container">
        <div className="tab-selector-tabs">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-selector-tab ${
                activeTab === tab.id ? "active" : ""
              }`}
              onClick={() => handleTabClick(tab.id)}
              aria-label={`${tab.label} tab`}
            >
              {tab.label}

              {activeTab === tab.id && badgeCounts?.[tab.id] > 0 && (
                <span
                  className="tab-selector-badge"
                  aria-label={`${badgeCounts[tab.id]} items`}
                >
                  {badgeCounts[tab.id]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Desktop/Tablet Search */}
        {withSearch && !isMobile && (
          <div className="tab-selector-search">
            <input
              type="text"
              className="tab-selector-search-input"
              placeholder="Search events..."
              value={searchQuery}
              onChange={handleSearchChange}
              aria-label="Search events"
            />
            {searchQuery ? (
              <button
                className="tab-selector-search-icon tab-selector-clear-button"
                onClick={handleSearchClear}
                aria-label="Clear search"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </button>
            ) : (
              <span className="tab-selector-search-icon" aria-hidden="true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                </svg>
              </span>
            )}
          </div>
        )}

        {withSearch && isMobile && (
          <div className="tab-selector-mobile-search">
            <button
              className={`tab-selector-mobile-search-button ${
                showMobileSearch ? "active" : ""
              }`}
              onClick={toggleMobileSearch}
              aria-label={showMobileSearch ? "Close search" : "Open search"}
              aria-expanded={showMobileSearch}
            >
              {showMobileSearch ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Search Expandable Input */}
      {withSearch && isMobile && showMobileSearch && (
        <form
          className="tab-selector-mobile-search-expanded"
          onSubmit={handleMobileSearchSubmit}
        >
          <div className="tab-selector-mobile-search-input-wrapper">
            <input
              type="text"
              className="tab-selector-mobile-search-input"
              placeholder="Search events..."
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
              aria-label="Search events"
            />
            {searchQuery && (
              <button
                type="button"
                className="tab-selector-mobile-clear-button"
                onClick={handleSearchClear}
                aria-label="Clear search"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </button>
            )}
          </div>
        </form>
      )}
    </>
  );
};

export default TabSelector;
